/**
 * Menus hook
 * TanStack Query hooks for weekly menu viewing and editing
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getWeekMenu,
  createMenu,
  updateMenu,
  deleteMenu,
} from '@/lib/api/menus';
import { getAvailabilitySummary } from '@/lib/api/availability';
import type {
  GetWeekMenuQuery,
  CreateMenuRequest,
  UpdateMenuRequest,
  GetAvailabilitySummaryQuery,
} from '@/types/api';
import { menuKeys } from '@/lib/query-keys';

// Re-export for backwards compatibility
export { menuKeys };

/**
 * Hook to fetch weekly menu
 *
 * Features:
 * - Get menu for a specific week (defaults to current week)
 * - Returns 7-day structure with lunch/dinner meals
 * - Includes week date range
 * - Used by members to view organizer's menu plan
 *
 * @example
 * ```tsx
 * function WeeklyMenu() {
 *   const { data, isLoading } = useWeekMenu({
 *     startDate: '2025-01-06', // Monday of the week
 *   });
 *
 *   if (isLoading) return <Spinner />;
 *
 *   return (
 *     <div>
 *       <h2>Week: {data.week.startDate} - {data.week.endDate}</h2>
 *       {data.menus.map(day => (
 *         <div key={day.date}>
 *           <p>{day.date}</p>
 *           <p>Lunch: {day.lunch?.meal.name || 'Not set'}</p>
 *           <p>Dinner: {day.dinner?.meal.name || 'Not set'}</p>
 *         </div>
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useWeekMenu(params?: GetWeekMenuQuery) {
  return useQuery({
    queryKey: menuKeys.week(params),
    queryFn: () => getWeekMenu(params),
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });
}

/**
 * Hook to fetch availability summary for menu editing
 *
 * Features:
 * - Get who's present for each day/meal in date range
 * - Used by organizers to plan menus based on attendance
 * - Includes user count for each slot
 *
 * @example
 * ```tsx
 * function MenuEditor() {
 *   const { data, isLoading } = useAvailabilitySummary({
 *     startDate: '2025-01-06',
 *     endDate: '2025-01-12',
 *   });
 *
 *   if (isLoading) return <Spinner />;
 *
 *   return (
 *     <div>
 *       {data.summary.map(item => (
 *         <div key={`${item.date}-${item.mealType}`}>
 *           {item.date} {item.mealType}: {item.presentUsers.length} people
 *         </div>
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useAvailabilitySummary(params: GetAvailabilitySummaryQuery) {
  return useQuery({
    queryKey: menuKeys.availability(params),
    queryFn: () => getAvailabilitySummary(params),
    enabled: !!(params.startDate && params.endDate),
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });
}

/**
 * Hook to create a menu entry
 *
 * Features:
 * - Create menu for specific date/meal
 * - Organizer-only operation
 * - Automatic cache invalidation
 * - Optimistic updates for better UX
 *
 * @example
 * ```tsx
 * function MenuSlot({ date, mealType }: { date: string; mealType: MealType }) {
 *   const createMutation = useCreateMenu();
 *   const { user } = useAuth();
 *
 *   const handleSelect = (mealId: number) => {
 *     createMutation.mutate({
 *       date,
 *       mealType,
 *       mealId,
 *       createdBy: user.id,
 *     });
 *   };
 *
 *   return (
 *     <button
 *       onClick={() => handleSelect(123)}
 *       disabled={createMutation.isPending}
 *     >
 *       Add meal
 *     </button>
 *   );
 * }
 * ```
 */
export function useCreateMenu() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateMenuRequest) => createMenu(data),
    onSuccess: () => {
      // Invalidate all week menu queries
      queryClient.invalidateQueries({
        queryKey: menuKeys.all,
      });
      // Invalidate menu suggestions cache
      queryClient.invalidateQueries({ queryKey: ['menu-suggestions'] });
    },
  });
}

/**
 * Hook to update a menu entry
 *
 * Features:
 * - Update meal for existing menu entry
 * - Organizer-only operation
 * - Automatic cache invalidation
 *
 * @example
 * ```tsx
 * function MenuSlot({ menu }: { menu: Menu }) {
 *   const updateMutation = useUpdateMenu();
 *
 *   const handleChange = (newMealId: number) => {
 *     updateMutation.mutate({
 *       id: menu.id,
 *       mealId: newMealId,
 *     });
 *   };
 *
 *   return (
 *     <button
 *       onClick={() => handleChange(456)}
 *       disabled={updateMutation.isPending}
 *     >
 *       Change meal
 *     </button>
 *   );
 * }
 * ```
 */
export function useUpdateMenu() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateMenuRequest }) =>
      updateMenu(id, data),
    onSuccess: () => {
      // Invalidate all week menu queries
      queryClient.invalidateQueries({
        queryKey: menuKeys.all,
      });
      // Invalidate menu suggestions cache
      queryClient.invalidateQueries({ queryKey: ['menu-suggestions'] });
    },
  });
}

/**
 * Hook to delete a menu entry
 *
 * Features:
 * - Remove menu entry for a slot
 * - Organizer-only operation
 * - Automatic cache invalidation
 *
 * @example
 * ```tsx
 * function MenuSlot({ menu }: { menu: Menu }) {
 *   const deleteMutation = useDeleteMenu();
 *
 *   const handleRemove = () => {
 *     deleteMutation.mutate(menu.id);
 *   };
 *
 *   return (
 *     <button
 *       onClick={handleRemove}
 *       disabled={deleteMutation.isPending}
 *     >
 *       Remove
 *     </button>
 *   );
 * }
 * ```
 */
export function useDeleteMenu() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteMenu(id),
    onSuccess: () => {
      // Invalidate all week menu queries
      queryClient.invalidateQueries({
        queryKey: menuKeys.all,
      });
      // Invalidate menu suggestions cache
      queryClient.invalidateQueries({ queryKey: ['menu-suggestions'] });
    },
  });
}
